
/**
 * O que faz: registra as rotas públicas e protegidas da aplicação.
 * Onde usa: ponto de entrada do React.
 * Conexões: provedores de autenticação, clínica, pipeline e carregamento lazy do Chat.
 * Tabelas: nenhuma consulta direta; páginas filhas acessam seus próprios hooks.
 * Data: 24/08/2026.
 */
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense } from "react";
import { FalhaCarregamentoChunk } from "@/components/layout/FalhaCarregamentoChunk";
import { carregarModuloComRetry } from "@/lib/recarregarAposFalhaDeChunk";
import { CarregamentoRota } from "@/components/layout/CarregamentoRota";
import { PipelineBoardSkeleton } from "@/components/pipeline/PipelineBoardSkeleton";
import { PipelinePageLazy } from "@/lib/rotas/rotaPipeline";
import { ClinicaProvider } from "@/contexts/ClinicaContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { PipelineProvider } from "@/contexts/PipelineContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { SubscriptionPaymentGate } from "@/components/subscription/SubscriptionPaymentGate";
import MainLayout from "@/components/layout/MainLayout";
import { EscopoVisualAdmin } from "@/components/admin/EscopoVisualAdmin";
import DashboardPage from "./pages/DashboardPage";
import ReturnsPage from "./pages/ReturnsPage";
// O painel de vendas só baixa seu código quando a rota é aberta.
const VendasPage = carregarModuloComRetry(() => import("./pages/VendasPage"));
import ContatosPage from "./pages/ContatosPage";
import AgendaPage from "./pages/AgendaPage";
import FollowupPage from "./pages/FollowupPage";
import BillingPage from "./pages/BillingPage";
const AdminPage = carregarModuloComRetry(() => import("./pages/AdminPage"));
const AdminGeneralDashboardPage = carregarModuloComRetry(() => import("./pages/AdminGeneralDashboardPage"));
const AdminClinicDetailsPage = carregarModuloComRetry(() => import("./pages/AdminClinicDetailsPage"));
const AdminMonitoringPage = carregarModuloComRetry(() => import("./pages/AdminMonitoringPage"));
const AdminDisparosPage = carregarModuloComRetry(() => import("./pages/AdminDisparosPage"));
const AdminTermometroPage = carregarModuloComRetry(() => import("./pages/AdminTermometroPage"));
// A rota de Configurações e suas abas não precisam entrar no bundle antes do primeiro acesso.
const ConfiguracoesPage = carregarModuloComRetry(() => import("./pages/ConfiguracoesPage"));
// O histórico de classificação só baixa seu código quando a pessoa abre os resultados.
// Carregada só ao abrir a rota, para o dashboard não baixar o módulo de conversas.
const AnaliseConversasPage = carregarModuloComRetry(() => import("./pages/AnaliseConversasPage"));
import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";
import WelcomePage from "./pages/WelcomePage";
import NotFound from "./pages/NotFound";
import ConfiguracoesIAPage from "./pages/ConfiguracoesIAPage";
const ScheduledMessagesPage = carregarModuloComRetry(() =>
  import("./pages/ScheduledMessagesPage").then((modulo) => ({ default: modulo.ScheduledMessagesPage })),
);
import LandingPage from "./pages/LandingPage";
import LandingPageV2 from "./pages/LandingPageV2";
import BlogIndexPage from "./pages/BlogIndexPage";
import BlogPostPage from "./pages/BlogPostPage";
import BlogCategoryPage from "./pages/BlogCategoryPage";
import ContentHubPage from "./pages/ContentHubPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import DataDeletionInstructionsPage from "./pages/DataDeletionInstructionsPage";
import PublicWhatsAppConnectPage from "./pages/PublicWhatsAppConnectPage";
const CaptionGeneratorPage = carregarModuloComRetry(() => import("./pages/CaptionGeneratorPage"));
const AdGeneratorPage = carregarModuloComRetry(() => import("./pages/AdGeneratorPage"));
const VideoAnalysisPage = carregarModuloComRetry(() => import("./pages/VideoAnalysisPage"));
const VideoConverterPage = carregarModuloComRetry(() => import("./pages/VideoConverterPage"));
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
const AgentChatPage = carregarModuloComRetry(() => import('@/pages/AgentChatPage'));
const MarketingAIPage = carregarModuloComRetry(() => import("./pages/admin/MarketingAIPage"));
const AIPromptsPage = carregarModuloComRetry(() => import("./pages/admin/AIPromptsPage"));
const AIModelsPage = carregarModuloComRetry(() => import("./pages/admin/AIModelsPage"));
const AdminAICostsPage = carregarModuloComRetry(() => import("./pages/admin/AdminAICostsPage"));
// Motivo: o diagnóstico detalhado só carrega quando o administrador abre a ferramenta.
const AdminLeadClassificationDebugPage = carregarModuloComRetry(() => import("./pages/admin/AdminLeadClassificationDebugPage"));
const BlogPostsPage = carregarModuloComRetry(() => import("./pages/admin/BlogPostsPage"));
import { SeoProfileAnalyzerPage } from "@/components/seo-profile/SeoProfileAnalyzerPage";
import { HelmetProvider } from "react-helmet-async";

// O Chat é carregado em chunk próprio para que o Pipeline não baixe histórico, áudio e previews antes do primeiro acesso.
const ChatPageWrapper = carregarModuloComRetry(() => import('./pages/ChatPageWrapper'));

function envolverRotaLazy(elemento: React.ReactNode, fallback: React.ReactNode = <CarregamentoRota />) {
  return (
    <FalhaCarregamentoChunk>
      <Suspense fallback={fallback}>{elemento}</Suspense>
    </FalhaCarregamentoChunk>
  );
}

const queryClient = new QueryClient();

// Persistir apenas o cache do dashboard (10 min)
if (typeof window !== "undefined") {
  const persister = createSyncStoragePersister({
    storage: window.localStorage,
  });

  persistQueryClient({
    queryClient,
    persister,
    maxAge: 10 * 60 * 1000,
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => query.queryKey[0] === "dashboardData",
    },
  });
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            {/* Aplica o Gens Design System só nas rotas /admin/* (inclusive modais em portal). */}
            <EscopoVisualAdmin />
            <Routes>
              {/*
               * =========================
               * ROTAS PÚBLICAS (sem login)
               * =========================
               * Importante: Todas as rotas públicas devem ficar AQUI no mesmo <Routes>.
               * Motivo: Se criarmos 2 <Routes> separados, o segundo (com "/*") também
               * vai "bater" na URL e pode redirecionar para /login, causando o problema
               * de "precisa fazer login" mesmo em uma página pública.
               */}

              {/* Página pública de conexão WhatsApp (link gerado por admin) */}
              <Route path="/conectar-whatsapp/:token" element={<PublicWhatsAppConnectPage />} />

              {/* Página pública de boas-vindas */}
              <Route path="/welcome" element={<WelcomePage />} />
              <Route path="/landing" element={<LandingPage />} />
              <Route path="/landing-v2" element={<LandingPageV2 />} />
              {/* Conteudo publico agent-ready para SEO, LLMs e captacao via WhatsApp. */}
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/blog/categoria/:slug" element={<BlogCategoryPage />} />
              <Route path="/crm-para-dentistas" element={<ContentHubPage hubSlug="crm-para-dentistas" />} />
              <Route path="/crm-para-clinicas-medicas" element={<ContentHubPage hubSlug="crm-para-clinicas-medicas" />} />
              <Route path="/crm-para-clinicas-esteticas" element={<ContentHubPage hubSlug="crm-para-clinicas-esteticas" />} />

              {/* Página pública de política de privacidade */}
              <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
              {/* Motivo: a Meta exige URLs públicas específicas para termos e exclusão de dados. */}
              <Route path="/termos-de-uso" element={<TermsOfServicePage />} />
              <Route path="/exclusao-de-dados" element={<DataDeletionInstructionsPage />} />

              {/* Rotas públicas de autenticação */}
              <Route path="/login" element={<SignInPage />} />
              <Route path="/signup" element={<SignUpPage />} />

              {/* Rotas públicas de recuperação de senha */}
              <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
              <Route path="/redefinir-senha" element={<ResetPasswordPage />} />

              {/* Redirecionar raiz para dashboard */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/*
               * =====================================
               * ROTAS PROTEGIDAS (exigem login)
               * =====================================
               */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      <SubscriptionPaymentGate>
                        <PipelineProvider>
                          <MainLayout />
                        </PipelineProvider>
                      </SubscriptionPaymentGate>
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              >
                {/* Rotas das páginas principais */}
                <Route path="dashboard" element={<DashboardPage />} />
                <Route
                  path="pipeline"
                  element={envolverRotaLazy(<PipelinePageLazy />, <PipelineBoardSkeleton />)}
                />
                <Route path="retornos" element={<ReturnsPage />} />
                <Route path="vendas" element={envolverRotaLazy(<VendasPage />)} />
                <Route path="analises/conversas" element={envolverRotaLazy(<AnaliseConversasPage />)} />
                <Route path="contatos" element={<ContatosPage />} />
                <Route
                  path="chat"
                  element={(
                    <FalhaCarregamentoChunk>
                      <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-slate-500">Carregando chat...</div>}>
                        <ChatPageWrapper />
                      </Suspense>
                    </FalhaCarregamentoChunk>
                  )}
                />
                <Route path="agenda" element={<AgendaPage />} />
                <Route path="ia" element={<ConfiguracoesIAPage />} />
                <Route path="mensagens-agendadas" element={envolverRotaLazy(<ScheduledMessagesPage />)} />
                <Route path="configuracoes" element={envolverRotaLazy(<ConfiguracoesPage />)} />
                <Route path="follow-up" element={<FollowupPage />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="agent" element={<AgentChatPage />} />
                <Route path="seo-perfil" element={<SeoProfileAnalyzerPage />} />
              </Route>

              {/* Gerador de Legendas - sem sidebar para melhor experiência mobile */}
              <Route
                path="/gerador-legendas"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      <SubscriptionPaymentGate>
                        {envolverRotaLazy(<CaptionGeneratorPage />)}
                      </SubscriptionPaymentGate>
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              {/* Gerador de Anúncios - PDF/URL based ad generation */}
              <Route
                path="/gerador-anuncios"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      <SubscriptionPaymentGate>
                        {envolverRotaLazy(<AdGeneratorPage />)}
                      </SubscriptionPaymentGate>
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              {/* Análise de Vídeo com IA - transcrição, visual e análise de anúncio */}
              <Route
                path="/analise-video"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      <SubscriptionPaymentGate>
                        {envolverRotaLazy(<VideoAnalysisPage />)}
                      </SubscriptionPaymentGate>
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              {/* Formatador de vídeo 9:16 → 4:5 com blur */}
              <Route
                path="/formatar-video"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      <SubscriptionPaymentGate>
                        {envolverRotaLazy(<VideoConverterPage />)}
                      </SubscriptionPaymentGate>
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              {/* Rota do admin (sem layout principal pois tem seu próprio layout) */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      <PipelineProvider>
                        {envolverRotaLazy(<AdminPage />)}
                      </PipelineProvider>
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              {/* Nova rota para o dashboard geral admin */}
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      <PipelineProvider>
                        {envolverRotaLazy(<AdminGeneralDashboardPage />)}
                      </PipelineProvider>
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              {/* Nova rota para a página de detalhes da clínica */}
              <Route
                path="/admin/clinicas/:clinicaId"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      <PipelineProvider>
                        {envolverRotaLazy(<AdminClinicDetailsPage />)}
                      </PipelineProvider>
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              {/* Rota para monitoramento admin */}
              <Route
                path="/admin/monitoring"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      <PipelineProvider>
                        {envolverRotaLazy(<AdminMonitoringPage />)}
                      </PipelineProvider>
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              {/* Central de disparos da API oficial do WhatsApp, protegida como as demais telas admin. */}
              <Route
                path="/admin/disparos"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      <PipelineProvider>
                        {envolverRotaLazy(<AdminDisparosPage />)}
                      </PipelineProvider>
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              {/* Liga/desliga geral do termômetro de engajamento; a página e as RPCs conferem se é admin. */}
              <Route
                path="/admin/termometro"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      {envolverRotaLazy(<AdminTermometroPage />)}
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              {/* Página 404 para rotas não encontradas */}
              <Route path="*" element={<NotFound />} />

              {/* Rota para Marketing AI (RAG) */}
              <Route
                path="/admin/marketing-ai"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      {envolverRotaLazy(<MarketingAIPage />)}
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/prompts-ia"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      {envolverRotaLazy(<AIPromptsPage />)}
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/modelos-ia"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      {envolverRotaLazy(<AIModelsPage />)}
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/custos-ia"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      {envolverRotaLazy(<AdminAICostsPage />)}
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/debug-classificacao-leads"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      {envolverRotaLazy(<AdminLeadClassificationDebugPage />)}
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/blog-posts"
                element={
                  <ProtectedRoute>
                    <ClinicaProvider>
                      {envolverRotaLazy(<BlogPostsPage />)}
                    </ClinicaProvider>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
